import { Link } from 'react-router-dom';
import { useMyDashboards } from '../api/dashboards.ts';
import { useProperties } from '../api/places.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';

/**
 * Real Estate, minimal until the property module proper (Arthur, 23 September).
 *
 * **One list and nothing else**: no dashboard, no metrics, no filters. Tier 1
 * only for now; the menu shows the entry to tier 1 alone and `GET /properties`
 * refuses anyone else. Every name is derived (`residence_name()`, 061), so it
 * is never edited here, and it follows the client's name if that changes.
 *
 * Every property is listed, not only the homes clients live in today: a house
 * a client owns while living in a nursing home is still the estate's.
 * "Lives here" says which are homes now.
 */
export function RealEstate() {
  const mine = useMyDashboards();
  const tierOne = mine.data?.tier === 1;

  return (
    <div className="min-h-dvh bg-neutral-50">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="border-b border-neutral-200 bg-white px-4 pt-3 pb-3 lg:rounded-t-lg lg:px-5 lg:pt-4 lg:shadow">
          <h1 className="text-xl font-bold text-neutral-900">Real Estate</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Every property on file, owned or rented, named after the client, the town and the kind of
            place.
          </p>
        </header>
        <div className="bg-white lg:rounded-b-lg lg:shadow">
          {mine.error && !mine.data ? (
            <LoadFailure what="Real Estate" error={mine.error} onRetry={() => void mine.refetch()} retrying={mine.isFetching} />
          ) : mine.isPending ? (
            <p className="px-4 py-10 text-center text-neutral-500">Loading…</p>
          ) : !tierOne ? (
            <div role="alert" className="p-6">
              <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
              <p className="mt-2 text-neutral-700">Real Estate is open to tier 1 only for now.</p>
              <Link
                to="/clients"
                className="mt-5 inline-flex min-h-[48px] items-center rounded-lg bg-neutral-900 px-5 font-semibold text-white"
              >
                Back to clients
              </Link>
            </div>
          ) : (
            <PropertyList />
          )}
        </div>
      </div>
    </div>
  );
}

function PropertyList() {
  const list = useProperties();

  if (list.error && !list.data) {
    return <LoadFailure what="The property list" error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  }
  if (!list.data) {
    return <p className="px-4 py-10 text-center text-neutral-500">Loading…</p>;
  }
  const rows = list.data.properties;
  if (rows.length === 0) {
    return <p className="px-4 py-10 text-center text-neutral-600">No property is on file.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <p className="px-4 pt-3 text-sm text-neutral-600 lg:px-5">
        {rows.length} {rows.length === 1 ? 'property' : 'properties'},{' '}
        {rows.filter((r) => r.lives_here).length} lived in by the client now.
      </p>
      <table className="mt-2 w-full text-left">
        <thead className="border-b border-neutral-200 text-xs font-bold tracking-[0.1em] text-neutral-500 uppercase">
          <tr>
            <th className="px-4 py-2 lg:px-5">Name</th>
            <th className="px-3 py-2">Address</th>
            <th className="px-3 py-2">Kind</th>
            <th className="px-3 py-2">Held</th>
            <th className="px-3 py-2">Lives here</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-neutral-100 align-top" data-quick-client={r.client_id}>
              <td className="px-4 py-2 lg:px-5">
                <Link
                  to={`/clients/${r.client_id}`}
                  state={{ from: 'Real Estate' }}
                  className="inline-flex min-h-[44px] items-center font-medium text-neutral-900 underline-offset-2 hover:underline"
                >
                  {r.name}
                </Link>
                <span className="block text-sm text-neutral-500">{r.client_ref} · {r.legal_status}</span>
              </td>
              <td className="px-3 py-2 text-neutral-800">
                {r.street ?? 'No street recorded'}
                <span className="block text-sm text-neutral-600">
                  {[r.city, [r.state, r.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                </span>
              </td>
              <td className="px-3 py-2 text-neutral-800">{r.property_type_label ?? 'Not recorded'}</td>
              <td className="px-3 py-2 text-neutral-800">
                {r.tenure === 'Unknown' ? 'Not known' : r.tenure}
                {r.disposition && <span className="block text-sm text-neutral-600">{r.disposition}</span>}
              </td>
              <td className="px-3 py-2 text-neutral-800">{r.lives_here ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
